import axios from 'axios';
import storejs from 'store'
import store from '../store/'
import _ from 'lodash';
import { 
  getServerUrl,
  isNewerVersion,
  inDevEnvironment,
} from '@utilities';

import {
  PLAYPOSIT_API_ENDPOINTS,
} from '@Constants';

let playerService = {
  // START config
  url: '',
  async setupAjax(token) {
    if (!this.url) this.url = getServerUrl();
    if (token) {
      // console.log('token!', token)
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      this.token = token;
    }
    axios.defaults.headers.common['Content-Type'] = 'application/json';
    axios.defaults.headers.common['Accept'] = 'application/json';
    axios.interceptors.request.use((config) => {
      if (config.method !== 'get' && _.get(store, 'state.player.user.readOnly') ) {
        console.log('prevent', config.url)
        store.commit('player/setSnackbar', {
          text: `Observers cannot make changes`,
          color: 'warning',
          timeout: 5000
        })
        return Promise.reject({
          label: 'read_only_unauthorized',
          data: {
            msg: 'this action is not allowed in read only mode'
          },
          status: 403,
          request_url: config.url,
          method: config.method
        });
      }
      return config;
    });
    axios.interceptors.response.use(
      response => {
        const storeVersion = storejs.get('playpositApplicationVersion');
        const serverVersion = _.get(response, 'headers[x-playposit-application-version]');
        if (
          !storeVersion
          && serverVersion
        ) storejs.set('playpositApplicationVersion', serverVersion);
        if (
          storeVersion
          && serverVersion
          && isNewerVersion(storeVersion, serverVersion)
        ) {
          storejs.set('playpositApplicationVersion', serverVersion);
          const outdatedSnackbarInterval = setInterval(() => {
            if (_.get(store, 'state.player.snackbar.enabled')) return;
            clearInterval(outdatedSnackbarInterval);
            store.commit('player/setSnackbar', {
              text: 'You are using an outdated version of PlayPosit. Please clear your cache and refresh the page.',
              color: 'warning',
              timeout: 10000,
              action_text: 'Refresh',
              action: () => {
                window.location.reload();
              }
            });
          }, 1000);
        }
        return response;
      },
      error => {
        if (_.has(error, 'config.headers.Authorization') && _.get(error, 'response.status', null) === 401) {
          // intercept unauthorized responses, attempt refresh token, then make original call again.
          // console.log('request unauthorized, try to reauthorize')
          const old_token = error.config.headers['Authorization'].replace('Bearer ', '');
          return playerService.refreshToken(old_token).then((res) => {
            // console.log('refreshed token')
            return axios.request(error.config);
          }).catch((retryErr) => {
            // console.log({message:`expired token: ${old_token}`});
            // console.log('user/lti', store.state.user, error.url);
            const errMsg = (() => {
              if (store.state.player.user && store.state.player.user.isLti) {
                // lti user, tell them to relaunch assignment
                return `https://www.playposit.com/msg?msg=${encodeURI('This session has expired, please return to your LMS and relaunch the assignment.')}&nobuttons=true`
              } else if (store.state.player.user && !store.state.player.user.isLti && !store.state.player.isPlaylist) {
                // regular user, give them button to login
                return `https://www.playposit.com/msg?msg=${encodeURI('This session has expired, please login to attempt again.')}&primary_text=login&primary_link=${encodeURI('https://www.playposit.com/login')}`;
              } else {
                // no user, should only trigger for getInitialPlayerAPI when there is not vuex user prop
                return `https://www.playposit.com/msg?msg=${encodeURI('This session has expired')}.&nobuttons=true`
              }
            })()
            store.state.player.tokenRefresh = true; // prevent alert during page refresh
            window.location = errMsg;
            return Promise.reject({msg:`expired token: ${old_token}`, redirect: errMsg})
          });
        }
        else {
          dataLayer.axiosLog(error)
          return Promise.reject(error);
        }
      },
    );
  },
  // END config
  // BEGIN player endpoints
  refreshToken(previous_token) {
    return axios.post(this.url + `authnew/refreshtoken`, {old_token: previous_token});
  },
  getInitialBulbData(type, query) {
    if (
      type === 'bulb'
      && query.bulb_id
      && query.section_id
      && this.token
    ) {
      const {
        bulb_id: bulbId,
        section_id: sectionId,
        resource_link_id: resourceLinkId,
        render_path: renderPath,
        playlist_id: playlistId,
        module_id: moduleId,
      } = query;
      return axios.get(
        PLAYPOSIT_API_ENDPOINTS.learnerPreview({
          url: this.url,
          bulbId,
          sectionId,
          resourceLinkId,
          renderPath,
          playlistId,
          moduleId,
          ...(query.lti13Launch && { lti13Launch: true })
        })
      );
    }
    else if (
      type === 'share'
      && query.bulb_id
      && this.token
    ) {
      const {
        bulb_id: bulbId,
      } = query;
      const sectionId = query?.section_id || 0;
      return axios.get(
        PLAYPOSIT_API_ENDPOINTS.teacherPreview({
          url: this.url,
          bulbId,
          sectionId,
        })
      );
    }
    else if (
      type === 'share'
      && query.bulb_id
      && !this.token
    ) {
      const {
        bulb_id: bulbId,
      } = query;
      return axios.get(
        PLAYPOSIT_API_ENDPOINTS.anonymousPreview({
          url: this.url,
          bulbId,
        })
      );
    }
    else if (
      type === 'parent'
      && query.student_id
      && this.token
    ) {
      const {
        bulb_id: bulbId,
        section_id: sectionId,
        student_id: studentId,
      } = query;
      return axios.get(
        PLAYPOSIT_API_ENDPOINTS.parentPreview({
          url: this.url,
          bulbId,
          sectionId,
          studentId,
        })
      );
    }
    else if (
      type === 'broadcast'
      && query.bulb_id
      && query.section_id
      && this.token
    ) {
      const {
        bulb_id: bulbId,
        resource_link_id: resourceLinkId,
        render_path: renderPath,
        lti13Launch,
      } = query;
      const sectionId = (query?.section_id || 0);
      return axios.get(
        PLAYPOSIT_API_ENDPOINTS.broadcastPreview({
          url: this.url,
          bulbId,
          sectionId,
          resourceLinkId,
          renderPath,
          lti13Launch,
        })
      );
    }
    else {
      let msg = '';
      let kbLink = 'https://knowledge.playposit.com/article/216-troubleshooting-bulb-playback'
     
      if (!['bulb', 'broadcast', 'parent', 'share'].includes(type)) {
        msg = `Invalid Player Type: ${type}`;
      }
      else if (type === 'bulb') {
        if (!query.bulb_id) msg += 'Missing bulb id\n';
        if (!query.section_id) msg +=  'Missing section id\n';
        if (!this.token) msg += 'Missing user token';
        kbLink += '#bulb-bluescreens';
      }
      else if (type === 'broadcast') {
        if (!query.bulb_id) msg += 'Missing bulb id\n';
        if (!query.section_id) msg += 'Missing section id\n';
        if (!this.token) msg += 'Missing user token';
        kbLink += '#broadcast-bluescreens';
      }
      else if (type === 'share' && !query.bulb_id) {
        msg = 'Missing a bulb id';
        kbLink += 'preview-bluescreens'
      }
      else if (type === 'parent') {
        if (!query.bulb_id) msg += 'Missing bulb id\n';
        if (!query.section_id) msg +=  'Missing section id\n';
        if (!query.student_id) msg += 'Missing observing id\n';
        if (!this.token) msg += 'Missing user token';
        kbLink += 'parent-bluescreens';
      }
      
      // redirect to bluescreen about incorrect params
      return Promise.reject({
        err: `invalid player query parameters`,
        blueScreen: {
          title: 'Cannot Preview',
          subtitle: `Invalid URL`,
          text: msg,
          buttons: [
            {
              text: 'More Info',
  						href: kbLink, 
  						order: 0
            }
          ],
        }
      })
    }
  },
  getBulbOwnerMigrationStatus(bulbId) {
    return axios.get(`${this.url}wevideo/migration-status/bulb/${bulbId}`);
  },
  getMediaAmpUrl(url) {
    /**
     * videojs doesn't attach the correct headers when requesting MediaAmp videos,
     * this helper pings MediaAmp and returns the correct full url for videoJS
     */
    return axios({
      url: `${url}?switch=m3u8&formats=m3u&format=redirect`,
      transformRequest: [(data, headers) => {
        if (headers?.common?.Authorization) {
          delete headers.common.Authorization;
        }
        if (headers?.Authorization) {
          delete headers.Authorization;
        }
        return data;
      }]
    });
  },
  getCaptions({
    captionUrl,
    type,
    teacherId,
    offsetTime = 0,
  } = {}) {
    if (!type) type = '';
    return axios({
      url: `${this.url}captions/proxy/${type}?url=${encodeURIComponent(captionUrl)}&teacher_id=${teacherId}&offset_time=${offsetTime}`,
      transformRequest: [(data, headers) => {
        if (headers?.common?.Authorization) {
          delete headers.common.Authorization;
        }
        if (headers?.Authorization) {
          delete headers.Authorization;
        }
        return data;
      }]
    });
  },
  // BEGIN interaction endpoints
  submitAnswer (data, gradeOnly, syncGrades) {
    return axios.post(`${this.url}neoplayer/${gradeOnly ? 'gradeanswer' : 'submitanswer'}`, data);
  },
  submitExplanation(data) {
    return axios.post(this.url + 'neoplayer/submitexplanation', data);
  },
  retryInteraction(data) {
    return axios.post(this.url + 'neoplayer/interaction/retry', data);
  },
  getInteractionAnalytics({
    interactionId,
    sectionId,
    cache,
    token,
  } = { }) {
    let domain = 'https://cache2.playposit.com/';
    if (inDevEnvironment() || !cache) domain = this.url;
    return axios.get(`${domain}neoplayer/interaction/${interactionId}/section/${sectionId}/chart.json?token=${token}`, {
      transformRequest: (data, headers) => {
        delete headers.common['Authorization'];
      },
      timeout: 3000,
    });
  },
  getInteractionAttemptCount(bulbAttemptId) {
    return axios.get(`${this.url}neoplayer/bulbattempt/interactionattempts/count/${bulbAttemptId}/`);
  },
  copyBulb(data) {
    /**
     * Request to make a copy of the bulb using exisiting NavService cloneLinkedLessons.
     */
     // [ ] check bulb listed permissions, perhaps create alternate route in PlayerService
    return axios.post(`${this.url}instructor/bulbs/${data.id}/copy/unrestricted`, data);
  },
  unlockBulbCheckout(bulbId) {
    return axios.get(`${this.url}bulb/checkout/${bulbId}/unlock`);
  },
  updateBulbViewCount(bulbId) {
    return axios.put(`${this.url}neoplayer/bulb/updateviewcount/${bulbId}`)
  },
  updateBulbAttemptTime(bulbAttemptId, updateData, config) {
    return axios.post(`${this.url}neoplayer/bulbAttempt/update/time/${bulbAttemptId}/${updateData.maxDisplayTime}`, updateData, config);
  },
  updateBulbAttempt(bulbAttemptId, updateData) {
    // update bulbAttempt
    return axios.post(`${this.url}neoplayer/bulbattempt/update/${bulbAttemptId}`, updateData);
  },
  updateSyncBulbAttempt(bulbAttemptId, updateData) {
    // update bulbAttempt and then sync the grades with lti
    return axios.post(`${this.url}neoplayer/bulbattempt/updatesync/${bulbAttemptId}`, updateData);
  },
  updateBulbAttemptSyncGoogle(data) {
    // update bulbAttempt and then sync the grades with GoogleClassroom
    return axios.post(`${this.url}neoplayer/bulbattempt/google-sync/${data.bulbAttemptId}`,
    {
      courseId: data.courseId,
      courseWorkId: data.courseWorkId,
      studentSubmissionId: data.studentSubmissionId,
      updateData: data.updateData,
    });
  },
  retryBulbAttempt(bulbAttemptId) {
    // archive bulbAttempt and associated interactionAttempts
    return axios.post(`${this.url}neoplayer/bulbattempt/retry/${bulbAttemptId}`);
  },
  syncLTIGrade (data) {
    return axios.post(`${this.url}neoplayer/bulbattempt/sync`, data)
  },
  syncLTIGradeStatus(data) {
    // check status of sync from lti2_logs
    return axios.post(`${this.url}neoplayer/bulbattempt/sync/status`, data);
  },
  updateStudent(userId, updateData) {
    return axios.put(`${this.url}neoplayer/student/update/${userId}`, updateData);
  },
  uploadVideo (data) {
    return axios.post(this.url + `upload/video`, data);
  },
  uploadImage (data) {
    return axios.post(this.url + `upload/image`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadAudio (data) {
    return axios.post(this.url + `upload/audio`, data);
  },
  getSectionNotes (bulbId, sectionId) {
    return axios.get(this.url + `neoplayer/notes/section/${bulbId}/${sectionId}`)
  },
  getAttemptNotes (bulbAttemptId) {
    return axios.get(this.url + `neoplayer/notes/attempt/${bulbAttemptId}`);
  },
  createNote (noteData) {
    return axios.post(this.url + `neoplayer/notes/create/`,noteData)
  },
  updateNote (noteId, noteData) {
    return axios.put(this.url + `neoplayer/notes/update/${noteId}`, noteData)
  },
  deleteNote (noteId) {
    return axios.delete(this.url + `neoplayer/notes/delete/${noteId}`);
  },
  submitRubric(rubricInteractionStates, data) {
    return axios.post(`${this.url}neoplayer/rubric/${data?.gradeOnly ? 'grade' : 'submit'}`, { ris: rubricInteractionStates, lti13Launch: data?.lti13Launch });
  },
  getAllDiscussionFromSection({
    interactionId,
    sectionId,
  } = {}) {
    return axios.get(`${this.url}neoplayer/discussion/${interactionId}/section/${sectionId}`);
  },
  getParentDiscussion(bulbAttemptId, interactionId) {
    return axios.get(`${this.url}neoplayer/discussion/${bulbAttemptId}/${interactionId}/parent`)
  },
  createComment (data, parentId, autograde, syncGrades) {
    return axios.post(this.url + `neoplayer/discussion/comment/create?syncGrades=${syncGrades}&autograde=${autograde}${(parentId) ? `&parentId=${parentId}` : ''}`, data);
  },
  updateComment (data) {
    return axios.put(this.url + `neoplayer/discussion/comment/update`, data);
  },
  deleteComment (commentId) {
    return axios.delete(this.url + `neoplayer/discussion/comment/delete/${commentId}`);
  },
  // END question endpoints
  // -- broadcast services starts
  getBroadcastLearnerDetails (learnerId) {
    return axios.get(this.url + `neoplayer/broadcast/student/${learnerId}`);
  },
  getBroadcastBulbAttemptDetails (bulbAttemptId) {
    return axios.get(this.url + `neoplayer/broadcast/bulb-attempt/${bulbAttemptId}`);
  },
  getBroadcastInteractionAttemptDetails (interactionId, bulbAttemptId, broadcastPage, broadcastPerPage) {
    return axios.get(this.url + `neoplayer/broadcast/interaction-attempt/${interactionId}/?bulb_attempt_ids=${bulbAttemptId}&broadcast_page=${broadcastPage}&broadcast_per_page=${broadcastPerPage}`);
  },
  // -- broadcast services ends
  apiLogger(message) {
    return axios.post(`${this.url}neoplayer/logger`, {message});
  },
  sendBeacon(beaconBulbId, beaconToken, beaconTimeElapsed, beaconMaxDisplayTime) {
    /*global fetch */
    fetch(`${this.url}neoplayer/beacon/${beaconBulbId}/?token=${beaconToken}&timeElapsed=${beaconTimeElapsed}&maxDisplayTime=${beaconMaxDisplayTime}`, {
        		keepalive: true,
        		method: 'POST',
  				credentials: 'include',
    });
  },
  createAttachment(data) {
    return axios.post(`${this.url}attachment`, data);
  },
  createAttachmentDownloadLog(data) {
    return axios.post(`${this.url}attachment/createDownloadLog`, data);
  },
  updateAttachment(data) {
    return axios.put(`${this.url}attachment`, data);
  },
  deleteAttachment({
    attachmentId,
    attachmentType,
    attachmentTypeId,
  } = {}) {
    return axios.delete(`${this.url}attachment/${attachmentId}/${attachmentType}${attachmentTypeId ? `/${attachmentTypeId}` : ''}`);
  },
  fetchGoogleStudentSubmissionId(data) {
    return axios.get(`${this.url}google-classroom/student-submission?courseWorkId=${data.courseWorkId}&courseId=${data.courseId}&bulbAttemptId=${data.bulbAttemptId}`);
  },
  downloadBulbResultsInDocxFormat({
    bulbId,
    studentId,
    sectionId,
  } = {}) {
    return axios.get(`${this.url}bulb/${bulbId}/worksheet/docx${studentId ? `/${studentId}` : ''}${sectionId ? `?sectionId=${sectionId}` : ''}`, {
      responseType: 'blob',
    });
  },
  getTeacherToken() {
    return axios.get(`${this.url}authnew/pd-redirect`);
  },
  sendProquestBulbAttemptUpdate({
    bulbAttemptId,
    event,
  } = {}) {
    try {
      return axios.post(`${this.url}proquest/data/bulbAttempt/${bulbAttemptId}/${event}`);
    } catch(e) {
      return e;
    }
  },
  getBroadcastAnalyticsData({
    interactionId,
    sectionId,
  } = {}) {
    try {
      return axios.get(`${this.url}neoplayer/broadcast-analytics/section/${sectionId}/interaction/${interactionId}`);
    } catch (e) {
      return e;
    }
  },
  createBulbAttemptView({
    bulbAttemptId,
  } = {}) {
    axios.post(`${this.url}neoplayer/bulb-attempt-view`,
      {
        bulbAttemptId,
      }
    ) 
  },
  convertUrlToPdf(url) {
    return axios.get(`https://url-to-pdf.lrnr.workers.dev?url=${encodeURIComponent(url)}`, {
      responseType: 'blob',
    });
  },
};

export default playerService;